import { z } from "zod";

/**
 * The deployment and build-job statuses a READER understands — HAND-LISTED,
 * deliberately not derived from the generated Prisma tuples the backend emits
 * from. That one difference is the whole mechanism.
 *
 * ── Why these two stay strict ──────────────────────────────────────────────
 *
 * Every other enum on a deployment read degrades to an unlisted value (see
 * `enum-read.schemas.ts`). A status cannot: readers make DECISIONS on it — is
 * the build still running and worth polling, may it be cancelled or rolled
 * back, did it ship, which beat of the pipeline is lit — and a value nobody can
 * classify has no truthful answer to any of them. Rendering it as a badge would
 * be honest; offering or withholding a cancel button on it would be a guess. So
 * a status this reader does not list still fails the parse.
 *
 * ── What protects an older reader instead ──────────────────────────────────
 *
 * Ordering. A new status must reach readers BEFORE the database can hold it:
 *
 *   1. Add it HERE, and give it words at every reader — the console's exhaustive
 *      Records and the CLI's `colorizeStatus` refuse to compile until it has
 *      them. Ship that.
 *   2. Only in a LATER release, add it to the Prisma enum and start writing it.
 *
 * `status-read-values-lead-the-database.test.ts` refuses the half that can be
 * refused from inside one tree: a database value this list lacks. Deriving this
 * list from the database tuple — as it once was — made step 1 impossible,
 * because the reader then learned a value in the same commit that let the
 * backend write it.
 *
 * debt: the spec proves the reader list is never BEHIND the database in one
 *       tree; it cannot prove step 1 SHIPPED before step 2, since both can land
 *       in one PR and the tree keeps no release history. Nor can any ordering
 *       reach a browser tab opened before step 1 deployed.
 *       Upgrade trigger: a release record the suite can read (a production tag
 *       or a deployed-bundle manifest), against which step 2 can be refused
 *       until step 1 is in it.
 */
export const VIBE_DEPLOYMENT_STATUS_READ_VALUES = [
  "BUILDING",
  "AWAITING_APPROVAL",
  "DEPLOYING",
  "HEALTHY",
  "FAILED",
  "ROLLED_BACK",
  "SUPERSEDED",
  "DISPLACED",
  "CANCELLED"
] as const;

export const VibeDeploymentStatusReadSchema = z.enum(VIBE_DEPLOYMENT_STATUS_READ_VALUES);
export type VibeDeploymentStatusReadValue = z.infer<typeof VibeDeploymentStatusReadSchema>;

/** Same mechanism, same two-step order, for the build job attached to a deployment. */
export const VIBE_BUILD_JOB_STATUS_READ_VALUES = [
  "PENDING",
  "QUEUED",
  "ADMITTED",
  "STARTING",
  "RUNNING",
  "SUCCEEDED",
  "FAILED",
  "TIMED_OUT",
  "CANCELLED",
  "SUPERSEDED",
  "LOST"
] as const;

export const VibeBuildJobStatusReadSchema = z.enum(VIBE_BUILD_JOB_STATUS_READ_VALUES);
export type VibeBuildJobStatusReadValue = z.infer<typeof VibeBuildJobStatusReadSchema>;

/**
 * Same mechanism, same two-step order, for every other status a reader DECIDES
 * on. Each one gates something a reader offers or withholds:
 *
 * - the git project — whether the app can deploy, and whether a provision retry
 *   is offered;
 * - the tenant cluster — whether it is usable, still converging, or being torn
 *   down — and its telemetry component, which decides whether app logs are
 *   offered;
 * - an approval request — whether a reviewer is offered approve and reject;
 * - an organization's cost safety — whether deploys are suspended.
 *
 * Every response that carries one of these reads it through the schema below,
 * never through the database-derived emitting enum, and
 * `status-read-values-lead-the-database.test.ts` holds each list ahead of its
 * database tuple.
 */
export const VIBE_GIT_PROJECT_STATUS_READ_VALUES = [
  "PENDING",
  "READY",
  "FAILED",
  "ARCHIVED"
] as const;
export const VibeGitProjectStatusReadSchema = z.enum(VIBE_GIT_PROJECT_STATUS_READ_VALUES);

export const VIBE_TENANT_CLUSTER_STATUS_READ_VALUES = [
  "PROVISIONING",
  "HEALTHY",
  "UPDATING",
  "DEGRADED",
  "DISABLING",
  "DISABLED_RETAINED",
  "DESTROYING",
  "DESTROYED"
] as const;
export const VibeTenantClusterStatusReadSchema = z.enum(VIBE_TENANT_CLUSTER_STATUS_READ_VALUES);

export const VIBE_TELEMETRY_STATUS_READ_VALUES = [
  "PENDING",
  "DEPLOYING",
  "HEALTHY",
  "FAILED"
] as const;
export const VibeTelemetryStatusReadSchema = z.enum(VIBE_TELEMETRY_STATUS_READ_VALUES);

export const VIBE_APPROVAL_REQUEST_STATUS_READ_VALUES = [
  "PENDING",
  "APPROVED",
  "REJECTED",
  "EXPIRED",
  "WITHDRAWN"
] as const;
export const VibeApprovalRequestStatusReadSchema = z.enum(VIBE_APPROVAL_REQUEST_STATUS_READ_VALUES);

export const VIBE_COST_SAFETY_STATUS_READ_VALUES = ["OK", "WARNING", "SUSPENDED"] as const;
export const VibeCostSafetyStatusReadSchema = z.enum(VIBE_COST_SAFETY_STATUS_READ_VALUES);
